import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Save, Plus, X, Mail, User, Trash2 } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { toast } from "sonner";

interface StudentEmail {
  id?: string;
  email: string;
  name: string;
}

interface StudentList {
  id: string;
  list_name: string;
  description: string | null;
  instructor_student_emails: Array<{
    id: string;
    email: string;
    student_name: string;
  }>;
}

export default function InstructorEditStudentList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { listId } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [formData, setFormData] = useState({
    listName: "",
    description: ""
  });
  const [students, setStudents] = useState<StudentEmail[]>([]);

  useEffect(() => {
    if (listId) {
      fetchStudentList();
    }
  }, [listId]);

  const fetchStudentList = async () => {
    if (!user || !listId) return;

    try {
      const { data, error } = await supabase
        .from('instructor_student_lists')
        .select(`
          id,
          list_name,
          description,
          instructor_student_emails (
            id,
            email,
            student_name
          )
        `)
        .eq('id', listId)
        .eq('instructor_id', user.id)
        .single();

      if (error) throw error;

      if (data) {
        setFormData({
          listName: data.list_name,
          description: data.description || ""
        });
        
        setStudents(
          data.instructor_student_emails.map(email => ({
            id: email.id,
            email: email.email,
            name: email.student_name
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching student list:', error);
      toast.error('Failed to load student list');
      navigate('/instructor/students');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddStudent = () => {
    setStudents(prev => [...prev, { email: "", name: "" }]);
  };

  const handleRemoveStudent = (index: number) => {
    setStudents(prev => prev.filter((_, i) => i !== index));
  };

  const handleStudentChange = (index: number, field: 'email' | 'name', value: string) => {
    setStudents(prev => prev.map((student, i) => 
      i === index ? { ...student, [field]: value } : student
    ));
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleUpdateList = async () => {
    if (!user || !listId) return;

    // Validation
    if (!formData.listName.trim()) {
      toast.error('List name is required');
      return;
    }

    const validStudents = students.filter(s => s.email.trim() && s.name.trim());
    if (validStudents.length === 0) {
      toast.error('At least one student email and name is required');
      return;
    }

    const invalidEmails = validStudents.filter(s => !validateEmail(s.email));
    if (invalidEmails.length > 0) {
      toast.error('Please enter valid email addresses for all students');
      return;
    }

    setLoading(true);
    try {
      // Update the student list
      const { error: listError } = await supabase
        .from('instructor_student_lists')
        .update({
          list_name: formData.listName.trim(),
          description: formData.description.trim() || null
        })
        .eq('id', listId);

      if (listError) throw listError;

      // Delete existing emails
      const { error: deleteError } = await supabase
        .from('instructor_student_emails')
        .delete()
        .eq('list_id', listId);

      if (deleteError) throw deleteError;

      // Add updated students
      const studentEmails = validStudents.map(student => ({
        list_id: listId,
        email: student.email.trim().toLowerCase(),
        student_name: student.name.trim()
      }));

      const { error: emailError } = await supabase
        .from('instructor_student_emails')
        .insert(studentEmails);

      if (emailError) throw emailError;

      toast.success('Student list updated successfully!');
      navigate('/instructor/students');
    } catch (error: any) {
      console.error('Error updating student list:', error);
      
      // Handle duplicate list name error
      if (error?.code === '23505' && error?.message?.includes('instructor_student_lists_instructor_id_list_name_key')) {
        toast.error('A student list with this name already exists. Please choose a different name.');
      } else {
        toast.error('Failed to update student list');
      }
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center">Loading...</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Edit Student List
          </h1>
          <p className="text-muted-foreground">
            Update your student list details and manage students.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>List Details</CardTitle>
                <CardDescription>
                  Update your student list information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="listName">List Name *</Label>
                  <Input
                    id="listName"
                    placeholder="e.g., Grade 6 Math Class A"
                    value={formData.listName}
                    onChange={(e) => handleInputChange('listName', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Optional description for this student list..."
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Student Emails</CardTitle>
                    <CardDescription>
                      Manage the students in this list
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleAddStudent}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Student
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {students.map((student, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 border rounded-lg">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Student Name
                        </Label>
                        <Input
                          placeholder="Enter student name"
                          value={student.name}
                          onChange={(e) => handleStudentChange(index, 'name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Email Address
                        </Label>
                        <Input
                          type="email"
                          placeholder="Enter email address"
                          value={student.email}
                          onChange={(e) => handleStudentChange(index, 'email', e.target.value)}
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveStudent(index)}
                      className="mt-8"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <div className="text-sm text-muted-foreground">
                  <p>• Changes will be saved when you update the list</p>
                  <p>• Make sure to use valid email addresses</p>
                  <p>• Removed students will no longer receive invitations</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button 
                onClick={handleUpdateList}
                disabled={loading}
                size="lg"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Updating...' : 'Update Student List'}
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                asChild
              >
                <Link to="/instructor/students">Cancel</Link>
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Edit Tips</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5 text-xs">1</Badge>
                  <p>Update the list name and description</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5 text-xs">2</Badge>
                  <p>Add or remove students as needed</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5 text-xs">3</Badge>
                  <p>Changes take effect immediately</p>
                </div>
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="mt-0.5 text-xs">4</Badge>
                  <p>Removed students won't receive future invitations</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}